import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";

/**
 * Represents an action that carries an operation type and a payload.
 * @template T - A generic object type for the payload.
 */
type Action<T extends Record<string, any>> = {
    operation: number; // Numeric identifier for the operation type.
    payload: T; // Data associated with the action.
};

/**
 * Function type that creates an Action<T>.
 * @template T - A generic object type for the payload.
 * @param payload - The data to include in the action.
 * @returns An Action<T> object.
 */
type ActionCreator<T extends Record<string, any>> = (...payload: any[]) => Action<T>;

/**
 * Dispatches an action by converting its payload into a LocalEvent. This should only be use to dispatch an action to update the store. This should not dispatch an action to the backend.
 * @template T - A generic object type for the action payload.
 * @param action - The action containing an operation and a payload.
 */
function dispatch<T extends Record<string, any>>(operation: number, payload?: T): void;
function dispatch<T extends Record<string, any>>(action: Action<T>): void;
function dispatch<T extends Record<string, any>>(arg1: number | Action<T>, arg2?: T): void {
    if (typeof arg1 === "number") {
        // If the first argument is a number, assume it's the `operation`, and second is the `payload`
        dispatch({ operation: arg1, payload: arg2 ?? {} });
        return;
    }

    if (!arg1 || typeof arg1 !== "object") return; // Ensure the action is valid.

    let action: Action<T> = arg1;
    let localEvent = new LocalEvent(action.operation); // Create a new event with the operation type.

    // Iterate through the payload properties and add them to the event.
    if (action.payload) {
        Object.entries(action.payload).forEach(([key, value]) => {
            localEvent.AddItem(key, value);
        });
    }

    LocalEventDispatcher.dispatch(localEvent); // Dispatch the event using the event dispatcher.
}

/**
 * Hook that returns the dispatch function.
 * Can be used in React-like environments for dependency injection.
 * @returns The dispatch function.
 */
function useDispatch(): typeof dispatch {
    return dispatch;
}

export { Action, ActionCreator, dispatch, useDispatch };
